/**
 * SAM-1327 (B18): the ID details of a new business user travel inside the POST /sam/users body (`identity`), because the
 * PATCH /crud/user the sheet used to send afterwards is a 403 for the staff that create users. Contract with the
 * lambda (sam-lambda-functions, create-user identity): every field optional, empty values dropped; the photos are the S3
 * keys the document scan answers (never a URL: no scheme, no leading «/», no «..»); any other key or a malformed value
 * is a 400 INVALID_IDENTITY and no account is created, so a value that would be refused is left out here instead.
 */
import type { CreateUserIdentity } from '../api/samApi';

const MAX = 100;
const DAY = /^\d{4}-\d{2}-\d{2}$/;

const s3Key = (raw?: string | null): string | undefined => {
  const v = (raw ?? '').trim();
  if (!v || v.length > MAX || v.startsWith('/') || v.includes('..') || /^[a-z][a-z0-9+.-]*:/i.test(v)) return undefined;
  return v;
};

const isoCountry = (raw?: string | null): string | undefined => {
  const v = (raw ?? '').trim();
  return /^[A-Za-z]{2}$/.test(v) ? v.toUpperCase() : undefined;
};

export const buildCreateUserIdentity = (p: {
  dob?: string;
  idType?: string;
  idNumber?: string;
  /** The ISO code the nationality picker resolved (an OCR'd text with no match is not a code). */
  nationality?: string;
  photoKey?: string;
  profileKey?: string;
}): CreateUserIdentity | undefined => {
  const idnum = (p.idNumber ?? '').trim();
  const identity: CreateUserIdentity = {
    birthdate: p.dob && DAY.test(p.dob) ? p.dob : undefined,
    idtype: idnum && p.idType ? p.idType.slice(0, MAX) : undefined,
    idnum: idnum ? idnum.slice(0, MAX) : undefined,
    nationality: isoCountry(p.nationality),
    photo_url: s3Key(p.photoKey),
    profile_picture: s3Key(p.profileKey),
  };
  const kept = Object.fromEntries(Object.entries(identity).filter(([, v]) => v)) as CreateUserIdentity;
  return Object.keys(kept).length ? kept : undefined;
};

export const ID_DETAILS_NOT_SAVED = 'ID details could not be saved';
